import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue
} from "@chakra-ui/react";
import { HSeparator } from "components/separator/Separator";
import DefaultAuth from "layouts/auth/Default";
import illustration from "assets/img/auth/mysportset.png";
import { LoginOutputDto } from "models/organization/dto/onboarding.dto";
import { FcGoogle } from "react-icons/fc";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { translate } from "../../i18n";
import { FEATURE_FLAGS } from "../../utils/feature-flags";
import { useAuth } from "./AuthProvider";
import { useSignIn } from "./hooks/useSignIn";
import RedirectAuthorizedUser from "./RedirectAuthorizedUser";
import { decodeJWT, isValidEmail, isValidPassword } from "./utils";

function SignInPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { currentUser, setCurrentUser } = useAuth();
  // `mutate`, NOT `mutateAsync` — Sentry DASHBOARD-1Q / DASHBOARD-Y.
  // The sign-in button's onClick used to call `mutateAsync` and hand its
  // promise to React, which discards it — so every mistyped password became an
  // UNHANDLED PROMISE REJECTION and was captured by Sentry's global handler.
  // Run 24's MutationCache filter (`utils/expectedErrors.ts`) suppresses the
  // EXPLICIT `captureException` only, so it could never close these two ids:
  // their events kept arriving on prod as `mechanism: onunhandledrejection`
  // for 16 days after that filter was deployed (newest 2026-10-12).
  // `mutate` returns void, so no promise can float. `authError` below is set
  // identically either way, so the operator still sees the failure message.
  const { mutate: signIn, isLoading: isSigningIn, error: authError } = useSignIn((data: LoginOutputDto) => {
    const decodedUser = decodeJWT(data.accessToken);
    setCurrentUser(decodedUser);
  });
  // Chakra color mode
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = useColorModeValue("gray.400", "gray.500");
  const textColorDetails = useColorModeValue("navy.700", "secondaryGray.600");
  const textColorBrand = useColorModeValue("brand.500", "white");
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const googleBg = useColorModeValue("secondaryGray.300", "whiteAlpha.200");
  const googleText = useColorModeValue("navy.700", "white");
  const googleHover = useColorModeValue(
    { bg: "gray.200" },
    { bg: "whiteAlpha.300" }
  );
  const googleActive = useColorModeValue(
    { bg: "secondaryGray.300" },
    { bg: "whiteAlpha.200" }
  );
  const [show, setShow] = useState(false);
  const handleClick = () => setShow(!show);
  if (currentUser) {
    return <RedirectAuthorizedUser user={currentUser} />
  }

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: "100%", md: "max-content" }}
        w='100%'
        mx={{ base: "auto", lg: "0px" }}
        me='auto'
        h='100%'
        alignItems='start'
        justifyContent='center'
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        mt={{ base: "40px", md: "14vh" }}
        flexDirection='column'>
        <Box me='auto'>
          <Heading color={textColor} fontSize='36px' mb='10px'>
            {translate('auth.signin')}
          </Heading>
          <Text
            mb='36px'
            ms='4px'
            color={textColorSecondary}
            fontWeight='400'
            fontSize='md'>
            {translate('auth.enter_info_to_sign_in')}
          </Text>
        </Box>
        <Flex
          zIndex='2'
          direction='column'
          w={{ base: "100%", md: "420px" }}
          maxW='100%'
          background='transparent'
          borderRadius='15px'
          mx={{ base: "auto", lg: "unset" }}
          me='auto'
          mb={{ base: "20px", md: "auto" }}>
          {FEATURE_FLAGS.IS_GOOGLE_AUTH_ENBLAED && <Button
            fontSize='sm'
            me='0px'
            mb='26px'
            py='15px'
            h='50px'
            borderRadius='16px'
            bg={googleBg}
            color={googleText}
            fontWeight='500'
            _hover={googleHover}
            _active={googleActive}
            _focus={googleActive}>
            <Icon as={FcGoogle} w='20px' h='20px' me='10px' />
            {translate('auth.sign_in_with_google')}
          </Button>}
          {FEATURE_FLAGS.IS_GOOGLE_AUTH_ENBLAED && <Flex align='center' mb='25px'>
            <HSeparator />
            <Text color={textColorSecondary} mx='14px'>
              {translate('auth.or')}
            </Text>
            <HSeparator />
          </Flex>}
          {authError && <Box mb='8px'><SignInErrorMessage /></Box>}
          <FormControl>
            <FormLabel
              display='flex'
              ms='4px'
              fontSize='sm'
              fontWeight='500'
              color={textColor}
              mb='8px'>
              {translate('auth.email')}<Text color={brandStars}>*</Text>
            </FormLabel>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              isRequired={true}
              variant='auth'
              fontSize='sm'
              ms={{ base: "0px", md: "0px" }}
              type='email'
              placeholder='jean@dujardin.com'
              mb='24px'
              fontWeight='500'
              size='lg'
            />

            <FormLabel
              ms='4px'
              fontSize='sm'
              fontWeight='500'
              color={textColor}
              display='flex'>
              {translate('auth.password')}<Text color={brandStars}>*</Text>
            </FormLabel>
            <InputGroup size='md'>
              <Input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                isRequired={true}
                fontSize='sm'
                placeholder='********'
                mb='24px'
                size='lg'
                type={show ? "text" : "password"}
                variant='auth'
              />
              <InputRightElement display='flex' alignItems='center' mt='4px'>
                <Icon
                  color={textColorSecondary}
                  _hover={{ cursor: "pointer" }}
                  as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                  onClick={handleClick}
                />
              </InputRightElement>
            </InputGroup>
            <Flex
            flexDirection='column'
            justifyContent='center'
            alignItems='end'
            maxW='100%'
            mt='-10px'
            mb='10px'>
            <Text color={textColorDetails} fontWeight='400' fontSize='14px'>
              <NavLink to='/auth/forgot-password'>
                <Text
                  color={textColorBrand}
                  as='span'
                  ms='5px'
                  fontWeight='500'>
                  {translate('auth.forgot_password')}?
                </Text>
              </NavLink>
            </Text>
          </Flex>
            <Button
              isLoading={isSigningIn}
              fontSize='sm'
              variant='brand'
              fontWeight='500'
              w='100%'
              isDisabled={!isValidEmail(email) || !isValidPassword(password)}
              h='50'
              onClick={() => signIn({ email, password })}
              mb='24px'>
              {translate('auth.signin')}
            </Button>
          </FormControl>
          <Flex
            flexDirection='column'
            justifyContent='center'
            alignItems='start'
            maxW='100%'
            mt='0px'>
            <Text color={textColorDetails} fontWeight='400' fontSize='14px'>
              {translate('auth.not_registered_yet')}
              <NavLink to='/auth/sign-up'>
                <Text
                  color={textColorBrand}
                  as='span'
                  ms='5px'
                  fontWeight='500'>
                  {translate('auth.create_an_account_cta')}
                </Text>
              </NavLink>
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

function SignInErrorMessage() {
  return (
    <Alert fontSize='sm' status="error" variant="left-accent">
      <AlertIcon />
      <AlertDescription>
        {translate('auth.sign_in_error_message')}
      </AlertDescription>
    </Alert>
  );
}
export default SignInPage;
